<script setup lang="ts">
import { computed, inject, onMounted, ref, shallowRef, triggerRef, ComponentPublicInstance, StyleValue, Ref } from 'vue'
import { useDisplay, useTheme } from 'vuetify'
import { NodeSelection } from '@tiptap/pm/state'
import { Node } from '@tiptap/pm/model'
import { Editor } from '@tiptap/core'

export type EditNodeDialogPayload = {
  editNodeDialog: boolean
  updateEditNodeDialog: (val: boolean) => void
  currentVariantParameters: Record<string, unknown>
  onUpdate: (update: Record<string, unknown>) => void
  onSave: () => void
}

interface Variant {
  tag: string
  label: string
  icon?: string
}

const props = defineProps<{
  currentVariant: string
  variants: Variant[]
  variantParameters: Record<string, unknown>
  nodeProps: { editor: Editor; getPos: () => number; node: Node }
  selected: boolean
}>()

const theme = useTheme()
const display = useDisplay()

const editNodeDialog = ref(false)
const editor = shallowRef(props.nodeProps.editor)
const elementContainer = ref<ComponentPublicInstance | null>(null)
const updatedVariantParameters: Ref<Record<string, unknown> | null> = ref(null)
const editorFocused = ref(inject<boolean>('isEditorFocused', false))

const editMode = computed(() => props.nodeProps.editor.isEditable)

const someNodeIsFocused = computed(() => {
  return editMode.value && editor.value.isFocused && editor.value.state.selection instanceof NodeSelection
})

const currentVariantObject = computed<Variant>(() => {
  return props.variants.find(v => v.tag === props.currentVariant) || { tag: '', label: '' }
})

const editCardStyle = computed<StyleValue>(() => {
  if (!elementContainer.value?.$el) return ''

  const elementRect = elementContainer.value.$el.getBoundingClientRect()
  const editorRect = props.nodeProps.editor.options.element.getBoundingClientRect()
  const minWidth = Math.min(display.width.value, 300)

  // Default to center
  let left = '50%'
  let right = 'auto'
  let transform = 'translate(-50%, 100%)'

  // Check left boundary
  if (elementRect.x + elementRect.width / 2 - editorRect.x < minWidth / 2) {
    left = `${-(elementRect.x - editorRect.x - 4)}px`
    transform = 'translate(0, 100%)'
  }
  // Check right boundary
  else if (elementRect.x + elementRect.width / 2 + minWidth / 2 > editorRect.width + editorRect.x) {
    left = 'auto'
    right = `${elementRect.x + elementRect.width - (editorRect.x + editorRect.width) + 4}px`
    transform = 'translate(0%, 100%)'
  }

  return {
    zIndex: 100,
    position: 'absolute' as const,
    bottom: 0,
    left,
    right,
    transform,
    width: `${minWidth}px`,
  }
})

const containerStyle = computed(() => {
  if (nodeFocused.value) {
    return { border: `4px solid ${theme.current.value.colors.secondary}` }
  }
  if (someNodeIsFocused.value && editMode.value) {
    return { border: '4px solid transparent', filter: 'blur(3px) grayscale(0.75)' }
  }
  return { border: '4px solid transparent' }
})

const nodeFocused = computed(() => editorFocused.value && props.selected)

async function switchToVariant(variant: string) {
  if (props.currentVariant === variant) return

  const from = props.nodeProps.getPos()
  const node = props.nodeProps.node
  const to = from + node.nodeSize - 1

  const variantToReplaceWith = props.variants.find(v => v.tag === variant)
  if (!variantToReplaceWith) return

  editor.value
    .chain()
    .focus()
    .insertContentAt(
      { from, to },
      {
        type: variantToReplaceWith.tag,
        attrs: props.variantParameters,
      }
    )
    .run()
}

function updateCurrentVariant() {
  if (!updatedVariantParameters.value) return
  const from = props.nodeProps.getPos()
  const node = props.nodeProps.node
  const to = from + node.nodeSize - 1

  editor.value
    .chain()
    .focus()
    .insertContentAt(
      { from, to },
      {
        type: props.currentVariant,
        attrs: updatedVariantParameters.value,
      }
    )
    .run()
  editNodeDialog.value = false
}

async function deleteElement() {
  editor.value.commands.deleteSelection()
}

onMounted(() => {
  editor.value.on('update', () => triggerRef(editor))
  editor.value.on('selectionUpdate', () => triggerRef(editor))
})
</script>

<template>
  <v-container
    fluid
    class="ma-0 pa-0 d-inline-block position-relative"
  >
    <v-container
      ref="elementContainer"
      class="ma-0 pa-2 rounded-lg"
      fluid
      :style="containerStyle"
    >
      <v-container
        class="ma-0 pa-0"
        fluid
        :style="editMode ? 'pointer-events: none; user-select: none;' : ''"
      >
        <slot
          ref="elementSlot"
          name="element"
        />
      </v-container>

      <v-layout-card
        v-if="nodeFocused"
        :style="editCardStyle"
        class="border-md border-primary rounded-pill pa-0"
      >
        <div class="d-flex justify-center align-center ga-2 px-4 py-2">
          <v-btn
            class="px-6"
            variant="flat"
            color="primary"
            rounded="pill"
            :prepend-icon="currentVariantObject.icon"
            append-icon="mdi-menu-down"
            @mousedown.prevent
          >
            {{ currentVariantObject.label }}
            <v-menu activator="parent">
              <v-list>
                <v-list-item
                  v-for="variant in variants"
                  :key="variant.tag"
                  class="font-weight-medium"
                  :value="variant.tag"
                  :prepend-icon="variant.icon"
                  density="compact"
                  slim
                  :active="variant.tag === currentVariant"
                  active-color="primary"
                  @mousedown.prevent
                  @click="switchToVariant(variant.tag)"
                >
                  {{ variant.label }}
                </v-list-item>
              </v-list>
            </v-menu>
          </v-btn>
          <v-btn
            size="small"
            variant="tonal"
            color="primary"
            icon
            rounded="pill"
            @mousedown.prevent
            @click="editNodeDialog = true"
          >
            <v-icon
              size="large"
              icon="mdi-pencil"
            />
          </v-btn>
          <v-btn
            size="small"
            variant="tonal"
            color="error"
            rounded="pill"
            icon
            @mousedown.prevent
            @click="deleteElement"
          >
            <v-icon
              size="large"
              icon="mdi-delete"
            />
          </v-btn>
        </div>
      </v-layout-card>
    </v-container>

    <slot
      v-if="editNodeDialog"
      name="nodeEditor"
      :edit-node-dialog="editNodeDialog"
      :update-edit-node-dialog="(val: boolean) => (editNodeDialog = val)"
      :current-variant-parameters="variantParameters"
      :on-update="(update: Record<string, unknown>) => (updatedVariantParameters = update)"
      :on-save="updateCurrentVariant"
    />
  </v-container>
</template>

<style scoped></style>
